import type {
  CreateThreadSpec,
  ThreadSpecModel,
  ThreadTableDeleteReport,
  ThreadTableImportReport,
  UpdateThreadSpec,
} from '@/types/thread.types';
import { apiClient } from './client';

/**
 * The CSV load parses and upserts the whole thread table in one request, so it gets more
 * room than the default; it is still far quicker than the hyperMILL tool-database import.
 */
const IMPORT_TIMEOUT_MS = 120000;

export const threadsApi = {
  /** GET /thread-specs - the whole thread table. Server-cached, so this is cheap to re-poll. */
  getAll: async (): Promise<ThreadSpecModel[]> => {
    const { data } = await apiClient.get<ThreadSpecModel[]>('/thread-specs');
    return data;
  },

  /**
   * POST /thread-specs/load-csv - upsert the thread table from the Kasko CSV export.
   *
   * Rows are matched on `designation`, so re-uploading a corrected file updates in place
   * rather than duplicating. The CSV is parsed server-side; nothing here reads its content.
   */
  importCsv: async (file: File): Promise<ThreadTableImportReport> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await apiClient.post<ThreadTableImportReport>('/thread-specs/load-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: IMPORT_TIMEOUT_MS,
    });
    return data;
  },

  /**
   * PUT /thread-specs/{id} - patch one thread spec.
   *
   * Only the changed fields belong in `patch`: an omitted key leaves the column untouched,
   * an explicit `null` clears one of the two nullable columns.
   */
  update: async (id: string, patch: UpdateThreadSpec): Promise<ThreadSpecModel> => {
    const { data } = await apiClient.put<ThreadSpecModel>(`/thread-specs/${id}`, patch);
    return data;
  },

  /** POST /thread-specs - add one thread. Omit `designation` to let the backend derive it. */
  create: async (body: CreateThreadSpec): Promise<ThreadSpecModel> => {
    const { data } = await apiClient.post<ThreadSpecModel>('/thread-specs', body);
    return data;
  },

  /**
   * GET /thread-specs/export - the whole table as the same CSV the import accepts.
   *
   * Asked for as a blob: the payload is a file to hand to the browser, not JSON to parse,
   * and the default response transform would mangle it.
   */
  exportCsv: async (): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>('/thread-specs/export', { responseType: 'blob' });
    return data;
  },

  /** DELETE /thread-specs/{id} - drop one thread. */
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/thread-specs/${id}`);
  },

  /** DELETE /thread-specs - drop every thread-table row. Returns how many went. */
  deleteAll: async (): Promise<ThreadTableDeleteReport> => {
    const { data } = await apiClient.delete<ThreadTableDeleteReport>('/thread-specs');
    return data;
  },
};
